import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Power, Ticket, Trash2 } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";

const usd = (cents) => "$" + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: cents % 100 ? 2 : 0 });
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString() : "—");
const EMPTY = { code: "", kind: "percent", value: "", label: "", expires_at: "", max_redemptions: "" };
const input = "bg-obsidian border border-[#262a3d] rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/60 w-full";

function statusOf(c) {
  if (!c.active) return ["paused", "text-slate-400 border-slate-600"];
  if (c.expires_at && new Date(c.expires_at) < new Date()) return ["expired", "text-coral border-coral/40 bg-coral/10"];
  if (c.max_redemptions && c.redemptions >= c.max_redemptions) return ["used up", "text-coral border-coral/40 bg-coral/10"];
  return ["live", "text-neon border-neon/40 bg-neon/10"];
}

export function CouponsTab() {
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminFetch("/api/admin/coupons").then(setCoupons).catch((e) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setBusy(true);
    try {
      const body = {
        code: form.code, kind: form.kind, value: Number(form.value), label: form.label,
        expires_at: form.expires_at || null,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      };
      const c = await adminFetch("/api/admin/coupons", { method: "POST", body: JSON.stringify(body) });
      setMsg(`Created ${c.code} — ${c.description}. Share it or link to the site with ?code=${c.code}`);
      setForm(EMPTY);
      load();
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  };

  const toggle = async (c) => {
    setMsg(""); setErr("");
    try {
      await adminFetch(`/api/admin/coupons/${c.code}`, { method: "PATCH", body: JSON.stringify({ active: !c.active }) });
      load();
    } catch (e2) { setErr(e2.message); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}? Customers won't be able to use it anymore (past redemptions stay on record).`)) return;
    setMsg(""); setErr("");
    try {
      await adminFetch(`/api/admin/coupons/${c.code}`, { method: "DELETE" });
      setMsg(`Deleted ${c.code}`);
      load();
    } catch (e2) { setErr(e2.message); }
  };

  return (
    <div className="space-y-6">
      <form data-testid="coupon-create-form" onSubmit={create} className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Create a coupon</p>
        <p className="text-xs text-slate-600 mb-4">Applies to the $197 Jingle Package at checkout. Hold music and bonus takes stay full price.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input data-testid="coupon-code" placeholder="Code (blank = auto, e.g. JINGLE-7F2A)" value={form.code}
                 onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${input} font-mono`} />
          <select data-testid="coupon-kind" value={form.kind} onChange={set("kind")} className={`${input} text-slate-300`}>
            <option value="percent">Percent off</option>
            <option value="amount">Dollars off</option>
          </select>
          <input data-testid="coupon-value" type="number" required min={1} max={form.kind === "percent" ? 100 : 197} step={1}
                 placeholder={form.kind === "percent" ? "Percent (e.g. 20)" : "Dollars (e.g. 50)"}
                 value={form.value} onChange={set("value")} className={input} />
          <input data-testid="coupon-label" placeholder="Campaign label (optional, e.g. Spring outreach)" value={form.label}
                 onChange={set("label")} className={input} />
          <label className="flex items-center gap-2 text-xs text-slate-500">
            <span className="shrink-0">Expires</span>
            <input data-testid="coupon-expires" type="date" value={form.expires_at} onChange={set("expires_at")} className={input} />
          </label>
          <input data-testid="coupon-max" type="number" min={1} step={1} placeholder="Max uses (blank = unlimited)"
                 value={form.max_redemptions} onChange={set("max_redemptions")} className={input} />
        </div>
        <p className="text-[11px] text-slate-600 mt-3">Every coupon is one use per business automatically.</p>
        <button data-testid="coupon-create-submit" type="submit" disabled={busy}
                className="mt-4 bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-6 py-3 rounded-xl text-sm hover:scale-[1.01] transition-transform cursor-pointer flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} CREATE COUPON
        </button>
      </form>
      {msg && <p data-testid="coupon-action-success" className="text-neon text-sm">{msg}</p>}
      {err && <p data-testid="coupon-action-error" className="text-coral text-sm">{err}</p>}

      <div className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Coupons</p>
        <div data-testid="coupon-list" className="space-y-2">
          {!coupons && <Loader2 className="h-5 w-5 animate-spin text-gold mx-auto" />}
          {coupons?.length === 0 && (
            <div className="border border-dashed border-[#262a3d] rounded-xl p-8 text-center">
              <Ticket className="h-6 w-6 text-slate-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">No coupons yet — create your first one above.</p>
            </div>
          )}
          {coupons?.map((c) => {
            const [status, cls] = statusOf(c);
            return (
              <div key={c.code} data-testid={`coupon-row-${c.code}`} className="flex items-center gap-3 flex-wrap border border-[#1d2030] rounded-xl px-4 py-3">
                <div className="flex-1 min-w-48">
                  <p className="text-sm font-bold font-mono tracking-wider">{c.code} <span className="text-gold font-sans font-normal">· {c.description}</span></p>
                  <p className="text-xs text-slate-500">
                    {c.label ? `${c.label} · ` : ""}
                    {c.redemptions}{c.max_redemptions ? `/${c.max_redemptions}` : ""} used
                    {c.discount_total_cents ? ` · ${usd(c.discount_total_cents)} discounted · ${usd(c.revenue_cents)} revenue` : ""}
                    {c.expires_at ? ` · expires ${fmtDate(c.expires_at)}` : ""}
                  </p>
                </div>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>{status}</span>
                <div className="flex gap-2">
                  <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} title={c.active ? "Pause" : "Resume"}
                          className={`transition-colors cursor-pointer ${c.active ? "text-slate-400 hover:text-white" : "text-coral hover:text-white"}`}>
                    <Power className="h-4 w-4" />
                  </button>
                  <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)} title="Delete"
                          className="text-slate-400 hover:text-coral transition-colors cursor-pointer">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
